import React, { useState } from 'react';
import { UploadCloud, FileText, AlertTriangle, CheckCircle, Loader2, ArrowRight } from 'lucide-react';
import { Dataset } from '../types';

const logoUrl = "/logonew.png";

const WelcomeScreen: React.FC<{
    onNavigateToLoader: (sourceType?: 'file' | 'paste' | 'url') => void;
    datasets?: Dataset[];
    onSelectDataset?: (id: string) => void
}> = ({ onNavigateToLoader, datasets = [], onSelectDataset }) => {

    return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 animate-in fade-in zoom-in duration-500">
            <div className="text-center mb-10">
                <div className="w-48 h-48 bg-gold-500/10 rounded-full flex items-center justify-center mx-auto mb-8 shadow-[0_0_40px_rgba(212,175,55,0.15)]">
                    <img src={logoUrl} alt="WeaverBI" className="w-32 h-32 object-contain" />
                </div>
                <h1 className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold-400 via-gold-200 to-gold-500 mb-4 drop-shadow-sm">
                    Welcome to WeaverBI
                </h1>
                <p className="text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed">
                    Import your data to begin analyzing.
                </p>
            </div>

            <button
                onClick={() => onNavigateToLoader('file')}
                className="relative w-full max-w-2xl h-64 rounded-3xl border-2 border-dashed border-gray-700 bg-obsidian-900/50 hover:border-gold-500/50 hover:bg-obsidian-800 transition-all duration-300 flex flex-col items-center justify-center p-8 group cursor-pointer shadow-[0_0_30px_rgba(0,0,0,0.2)] hover:shadow-[0_0_30px_rgba(212,175,55,0.1)]"
            >
                <div className="w-24 h-24 rounded-full bg-obsidian-950 flex items-center justify-center mb-6 shadow-inner border border-gold-500/20 group-hover:scale-110 transition-transform duration-300">
                    <UploadCloud className="w-10 h-10 text-gray-400 group-hover:text-gold-400 transition-colors" />
                </div>
                <p className="text-2xl font-medium text-gray-300 mb-2 group-hover:text-white transition-colors">
                    Start Importing Data
                </p>
                <p className="text-sm text-gray-500 flex items-center gap-2 group-hover:text-gold-400/80 transition-colors">
                    CSV, JSON, Logs, or URL <ArrowRight className="w-4 h-4 ml-1" />
                </p>
            </button>

            {datasets.length > 0 && (
                <div className="mt-12 w-full max-w-2xl animate-in fade-in slide-in-from-bottom-8 duration-700 delay-200">
                    <h3 className="text-gray-400 text-sm font-medium uppercase tracking-wider mb-4 text-center">Recent Datasets</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {datasets.slice(-4).reverse().map(ds => (
                            <button
                                key={ds.id}
                                onClick={() => onSelectDataset?.(ds.id)}
                                className="flex items-center gap-3 p-4 rounded-xl bg-obsidian-900/50 border border-gold-500/10 hover:border-gold-500/30 hover:bg-obsidian-800 transition-all group text-left"
                            >
                                <div className="w-10 h-10 rounded-lg bg-obsidian-950 flex items-center justify-center border border-gold-500/10 group-hover:border-gold-500/30">
                                    <FileText className="w-5 h-5 text-gray-500 group-hover:text-gold-400 transition-colors" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-gray-200 font-medium truncate group-hover:text-gold-200 transition-colors">{ds.name}</p>
                                    <p className="text-xs text-gray-500">{ds.sampleRows.length} rows</p>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default WelcomeScreen;
